import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Grid,
  Chip,
} from '@mui/material';
import { Close, Settings, Add, Delete, Edit, ExpandMore, RemoveRedEye } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { formatAppDateTime } from '../../services/appConfigRuntime';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`;
const PARAM_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-parameters`;
const CONTAINERS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-containers`;
const CONTAINER_ASSIGNMENTS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-container-assignments`;
const ASSIGNED_CONTAINERS_API_URL = `${CONTAINER_ASSIGNMENTS_API_URL}/sync-file`;
const TRACK_RECORD_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-tracks/flow`;
const ERROR_RECORD_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-error-records/sfl`;
const SYSTEM_ERROR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-errors/flow`;
const CONNECTOR_ASSIGNMENT_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-connector-assignment`;

// UUID generator function for compatibility across browsers and Node.js
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments where crypto.randomUUID is not available
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const RUN_STATUS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync/runs`;
const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;
const DATA_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/data`;
const DEFAULT_PAGE_SIZE = 1000;
/** Rows-per-page choices for flow drawer data tables (must include DEFAULT_PAGE_SIZE). */
const TABLE_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 500, 1000];

const AD_EXPENSE_TO_QBO_FLOW_NAME = 'Autodesk Expense To QuickBooks Expense';

const PARAMETER_DISPLAY_NAMES = {
  ItemRef: 'Item Account Id',
  AccountRef: 'Account Reference',
  ExpenseAccountRef: 'Expense Account Id',
  IncomeAccountRef: 'Income Account Id',
};

const getParameterDisplayName = (technicalName) => {
  if (!technicalName) return '';
  return PARAMETER_DISPLAY_NAMES[technicalName] ?? technicalName;
};

const token = localStorage.getItem('token');

const SyncFlowParameter = ({ open, onClose, masterFlowId, masterFlowName, instanceId, uiConfig }) => {
  const [parameters, setParameters] = useState([]);
  const [trackRecords, setTrackRecords] = useState([]);
  const [errorRecords, setErrorRecords] = useState([]);
  const [systemErrors, setSystemErrors] = useState([]);
  const [runStatusRecords, setRunStatusRecords] = useState([]);
  const [errorRecordsPage, setErrorRecordsPage] = useState(0);
  const [errorRecordsPageSize, setErrorRecordsPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [errorRecordsTotal, setErrorRecordsTotal] = useState(0);
  const [systemErrorsPage, setSystemErrorsPage] = useState(0);
  const [systemErrorsPageSize, setSystemErrorsPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [systemErrorsTotal, setSystemErrorsTotal] = useState(0);
  const [runStatusPage, setRunStatusPage] = useState(0);
  const [runStatusPageSize, setRunStatusPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [runStatusTotal, setRunStatusTotal] = useState(0);
  const [trackRecordsPage, setTrackRecordsPage] = useState(0);
  const [trackRecordsPageSize, setTrackRecordsPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [trackRecordsTotal, setTrackRecordsTotal] = useState(0);
  const [dataRecordsPage, setDataRecordsPage] = useState(0);
  const [dataRecordsPageSize, setDataRecordsPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dataRecordsTotal, setDataRecordsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [paramValue, setParamValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [availableParameters, setAvailableParameters] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState('');
  const [containers, setContainers] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [assignContainerModalOpen, setAssignContainerModalOpen] = useState(false);
  const [assignedContainers, setAssignedContainers] = useState([]);
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [selectedResponseJson, setSelectedResponseJson] = useState(null);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState('parameters');
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [dataRecords, setDataRecords] = useState([]);
  const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [assignedConnectorsData, setAssignedConnectorsData] = useState([]);
  const [availableConnectorsForAssignment, setAvailableConnectorsForAssignment] = useState([]);
  const [connectorModalOpen, setConnectorModalOpen] = useState(false);
  const [selectedConnectorType, setSelectedConnectorType] = useState('');
  const [selectedConnectorId, setSelectedConnectorId] = useState('');
  const [loadingAssignedConnectors, setLoadingAssignedConnectors] = useState(false);

  const sortedRunStatusRecords = useMemo(() => {
    return [...runStatusRecords].sort((a, b) => {
      const timeA = new Date(a.srsStartDateTime || a.srsEndDateTime || 0).getTime();
      const timeB = new Date(b.srsStartDateTime || b.srsEndDateTime || 0).getTime();
      return timeB - timeA;
    });
  }, [runStatusRecords]);

  // flowIdentity: for APIs that need "this instance" use instanceId when present (sfi_id), else masterFlowId
  const flowIdentity = instanceId || masterFlowId;

  useEffect(() => {
    if (!open || !masterFlowId) return;
    setTrackRecordsPage(0);
    setDataRecordsPage(0);
    setErrorRecordsPage(0);
    setSystemErrorsPage(0);
    setRunStatusPage(0);
  }, [open, masterFlowId, instanceId]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchParameters();
      // Master Flow UI config: skip containers fetch when section is disabled
      if (uiConfig?.sections?.containers !== false) {
        fetchContainers();
        fetchAssignedContainers();
      }
      fetchAssignedConnectors();
      fetchMasterConnectors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, masterFlowId, instanceId, masterFlowName, uiConfig]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchTrackRecords();
    }
  }, [open, masterFlowId, instanceId, trackRecordsPage, trackRecordsPageSize]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchErrorRecords();
    }
  }, [open, masterFlowId, instanceId, errorRecordsPage, errorRecordsPageSize]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchSystemErrors();
    }
  }, [open, masterFlowId, instanceId, systemErrorsPage, systemErrorsPageSize]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchRunStatusRecords();
    }
  }, [open, masterFlowId, instanceId, runStatusPage, runStatusPageSize]);

  useEffect(() => {
    if (open && masterFlowId) {
      fetchDataRecords();
    }
  }, [open, masterFlowId, instanceId, dataRecordsPage, dataRecordsPageSize]);

  const fetchMasterConnectors = async () => {
    try {
      // masterFlowId prop is actually the master flow ID (sfi_mf_id), not the instance ID
      // We can directly use it to get the connectors without fetching the flow instance

      console.log('Fetching connectors for master flow ID:', masterFlowId);

      if (!masterFlowId) {
        console.warn('No master flow ID provided, showing all connectors');
        const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
        const allConnectors = await allConnectorsResponse.json();
        setMasterConnectors(Array.isArray(allConnectors) ? allConnectors : []);
        return;
      }

      // Get the connector IDs used by this master flow
      const connectorsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow/${masterFlowId}/connectors`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!connectorsResponse.ok) {
        console.warn(`Could not fetch flow connectors (status: ${connectorsResponse.status}), showing all connectors`);
        // Fallback to showing all connectors
        const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
        const allConnectors = await allConnectorsResponse.json();
        setMasterConnectors(Array.isArray(allConnectors) ? allConnectors : []);
        return;
      }

      const connectorIds = await connectorsResponse.json();
      console.log('Connector IDs used by this flow:', connectorIds);

      // Fetch all master connectors and filter by the IDs used in this flow
      const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
      const allConnectors = await allConnectorsResponse.json();

      // Ensure allConnectors is an array
      const connectorsArray = Array.isArray(allConnectors) ? allConnectors : [];

      // Filter to only show connectors used by this flow
      const flowConnectors = connectorsArray.filter(connector =>
        connectorIds.includes(connector.mcId)
      );

      console.log(`Flow uses ${flowConnectors.length} connector types:`, flowConnectors.map(c => c.mcName));
      setMasterConnectors(flowConnectors);
    } catch (error) {
      console.error('Error fetching master connectors:', error);
      toast.error(`Failed to load master connectors: ${error.message}`);
    }
  };

  const fetchParameters = async () => {
    if (!masterFlowId) return;
    setLoading(true);
    try {
      const url = instanceId
        ? `${API_BASE_URL}/${masterFlowId}?instanceId=${instanceId}`
        : `${API_BASE_URL}/${masterFlowId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setParameters(data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackRecords = async () => {
    if (!flowIdentity) return;
    setLoading(true);
    try {
      // When drawer is for an instance, send instanceId so backend returns only that instance's tracks (st_instance_id)
      const hasInstance = instanceId != null && String(instanceId).trim() !== '';
      const trackUrl = hasInstance
        ? `${TRACK_RECORD_API_URL}/${flowIdentity}?instanceId=${instanceId}&page=${trackRecordsPage}&size=${trackRecordsPageSize}`
        : `${TRACK_RECORD_API_URL}/${flowIdentity}?page=${trackRecordsPage}&size=${trackRecordsPageSize}`;
      const response = await fetch(trackUrl,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,

          }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      setTrackRecords(list);
      setTrackRecordsTotal(isPaginated ? Number(data.total) || 0 : list.length);
    } catch (error) {
      console.error('Error fetching track records:', error);
      toast.error(`Failed to load track records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrorRecords = async () => {
    if (!flowIdentity) return;
    setLoading(true);
    try {
      const url = `${ERROR_RECORD_API_URL}/${flowIdentity}?page=${errorRecordsPage}&size=${errorRecordsPageSize}`;
      const response = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,

          }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      setErrorRecords(list);
      setErrorRecordsTotal(isPaginated ? Number(data.total) || 0 : list.length);
    } catch (error) {
      console.error('Error fetching error records:', error);
      toast.error(`Failed to load error records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemErrors = async () => {
    if (!flowIdentity) return;
    setLoading(true);
    try {
      const url = `${SYSTEM_ERROR_API_URL}/${flowIdentity}?page=${systemErrorsPage}&size=${systemErrorsPageSize}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      setSystemErrors(list);
      setSystemErrorsTotal(isPaginated ? Number(data.total) || 0 : list.length);
      console.log(`Fetched ${list.length} system errors for flow: ${flowIdentity}`);
    } catch (error) {
      console.error('Error fetching system errors:', error);
      toast.error(`Failed to load system errors: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchRunStatusRecords = async () => {
    if (!masterFlowId) return;
    setLoading(true);
    try {
      const runStatusUrl = instanceId
        ? `${RUN_STATUS_API_URL}?flowId=${masterFlowId}&instanceId=${instanceId}&page=${runStatusPage}&size=${runStatusPageSize}`
        : `${RUN_STATUS_API_URL}?flowId=${masterFlowId}&page=${runStatusPage}&size=${runStatusPageSize}`;
      const response = await fetch(runStatusUrl,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,

          }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      setRunStatusRecords(list);
      setRunStatusTotal(isPaginated ? Number(data.total) || 0 : list.length);
    } catch (error) {
      console.error('Error fetching run status records:', error);
      toast.error(`Failed to load run status records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedContainers = async () => {
    if (!flowIdentity) return;
    setLoading(true);
    try {
      const response = await fetch(`${ASSIGNED_CONTAINERS_API_URL}/${flowIdentity}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,

          }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAssignedContainers(data);
      const assignedConIds = data.map((assignment) => assignment.conId);
      setSelectedContainers(assignedConIds);
    } catch (error) {
      console.error('Error fetching assigned containers:', error);
      toast.error(`Failed to load assigned containers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchContainers = async () => {
    setLoadingContainers(true);
    try {
      // Sync Flow: Fetch containers with tenant filter (instanceId = null)
      // This will return all shared containers (instance_id = NULL), regardless of target_id
      const tenantId = localStorage.getItem('tenantId');
      const url = `${CONTAINERS_API_URL}/tenant/${tenantId}`;

      console.log('Sync Flow - Fetching containers from:', url);

      const response = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,

          }
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      const data = await response.json();
      console.log('Sync Flow - Fetched containers:', data);
      console.log('Sync Flow - Number of containers:', data?.length || 0);
      setContainers(data || []);
    } catch (error) {
      console.error('Error fetching containers:', error);
      toast.error(`Failed to load containers: ${error.message}`);
      setContainers([]); // Ensure containers is set to empty array on error
    } finally {
      setLoadingContainers(false);
    }
  };

  const fetchAssignedConnectors = async () => {
    if (!masterFlowId) return;
    setLoadingAssignedConnectors(true);
    try {
      const connectorUrl = instanceId
        ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}?instanceId=${instanceId}`
        : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}`;
      const response = await fetch(connectorUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAssignedConnectorsData(data);
      console.log(`Fetched ${data.length} assigned connectors for flow: ${masterFlowId}${instanceId ? ` (instance: ${instanceId})` : ''}`);
    } catch (error) {
      console.error('Error fetching assigned connectors:', error);
      toast.error(`Failed to load assigned connectors: ${error.message}`);
    } finally {
      setLoadingAssignedConnectors(false);
    }
  };

  const handleOpenConnectorModal = (connectorType) => {
    setSelectedConnectorType(connectorType);
    setSelectedConnectorId('');
    fetchAvailableConnectorsForType(connectorType);
    setConnectorModalOpen(true);
  };

  const fetchAvailableConnectorsForType = async (connectorType) => {
    setLoadingAssignedConnectors(true);
    try {
      const tenantId = localStorage.getItem('tenantId');

      // Find master connector by name
      const masterConnector = masterConnectors.find(mc => mc.mcName === connectorType);
      if (!masterConnector) {
        toast.error('Master connector not found');
        return;
      }

      const response = await fetch(
        `${CONNECTOR_ASSIGNMENT_API_URL}/available/${masterConnector.mcId}?tenantId=${tenantId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAvailableConnectorsForAssignment(data);
    } catch (error) {
      console.error('Error fetching available connectors:', error);
      toast.error(`Failed to load available connectors: ${error.message}`);
    } finally {
      setLoadingAssignedConnectors(false);
    }
  };

  const handleAssignConnector = async () => {
    if (!selectedConnectorId) {
      toast.error('Please select a connector');
      return;
    }

    try {
      const tenantId = localStorage.getItem('tenantId');

      const response = await fetch(CONNECTOR_ASSIGNMENT_API_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          flowId: masterFlowId,
          ...(instanceId && { instanceId }),
          connectorType: selectedConnectorType,
          connectorId: selectedConnectorId,
          tenantId: parseInt(tenantId)
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      toast.success(`Connector assigned successfully`);
      setConnectorModalOpen(false);
      setSelectedConnectorId('');
      setSelectedConnectorType('');
      fetchAssignedConnectors();
    } catch (error) {
      console.error('Error assigning connector:', error);
      toast.error(`Failed to assign connector: ${error.message}`);
    }
  };

  const handleRemoveConnectorAssignment = async (connectorType) => {
    try {
      const removeUrl = instanceId
        ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}/type/${connectorType}?instanceId=${instanceId}`
        : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}/type/${connectorType}`;
      const response = await fetch(removeUrl,
        {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      toast.success(`Connector assignment removed`);
      fetchAssignedConnectors();
    } catch (error) {
      console.error('Error removing connector assignment:', error);
      toast.error(`Failed to remove assignment: ${error.message}`);
    }
  };

  const fetchAvailableParameters = async () => {
    if (!masterFlowId) return;
    setLoadingParameters(true);
    try {
      const response = await fetch(`${PARAM_API_URL}/flow/${masterFlowId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const params =
        masterFlowName === AD_EXPENSE_TO_QBO_FLOW_NAME
          ? data.filter((p) => p.mfpName !== 'PurchaseAccountRef')
          : data;
      setAvailableParameters(params);
    } catch (error) {
      console.error('Error fetching available parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoadingParameters(false);
    }
  };

  const fetchDataRecords = async () => {
    if (!flowIdentity) return;
    setLoading(true);
    try {
      const url = `${DATA_API_URL}/flow/${flowIdentity}?page=${dataRecordsPage}&size=${dataRecordsPageSize}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      setDataRecords(list);
      setDataRecordsTotal(isPaginated ? Number(data.total) || 0 : list.length);
    } catch (error) {
      console.error('Error fetching data records:', error);
      toast.error(`Failed to load data records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setModalOpen(true);
    setSelectedParameter('');
    setParamValue('');
    fetchAvailableParameters();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedParameter('');
    setParamValue('');
  };

  const handleCloseAssignContainerModal = () => {
    setAssignContainerModalOpen(false);
    setSelectedContainer('');
  };

  const handleParameterSelect = (e) => {
    const selectedParamId = e.target.value;
    setSelectedParameter(selectedParamId);
    const selectedParam = availableParameters.find((p) => p.mfpId === selectedParamId);
    if (selectedParam) {
      const parameterName = selectedParam.mfpName;
      const connectorMapping = {
        QuickbookConnectionId: 'Quickbook',
        AutodeskConnectionId: 'Autodesk',
        AutodeskDocsConnectionId: 'Autodesk Docs',
        SalesforceConnectionId: 'Salesforce',
        FusionConnectionId: 'FusionOperation',
        DynamicsConnectionId: 'Business centre',
      };

      if (parameterName in connectorMapping) {
        const connectorName = connectorMapping[parameterName];
        const connector = masterConnectors.find((mc) => mc.mcName === connectorName);
        if (connector) {
          setParamValue(connector.mcId);
        } else {
          setParamValue('');
        }
      } else {
        setParamValue('');
      }
    } else {
      setParamValue('');
    }
  };

  const handleSaveContainers = async (targetContainers) => {
    if (!masterFlowId) {
      toast.warning('Master Flow ID is required');
      return;
    }
    setLoading(true);
    try {
      const unassignPromises = assignedContainers
        .filter((assignment) => !targetContainers.includes(assignment.conId))
        .map(async (assignment) => {
          const response = await fetch(`${CONTAINER_ASSIGNMENTS_API_URL}/${assignment.scaId}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        });

      await Promise.all(unassignPromises);

      const assignPromises = targetContainers
        .filter((conId) => !assignedContainers.some((assignment) => assignment.conId === conId))
        .map(async (conId) => {
          const assignmentData = {
            conId,
            sflId: flowIdentity,
            ...(instanceId && { scaInstanceId: instanceId }),
          };
          const response = await fetch(CONTAINER_ASSIGNMENTS_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(assignmentData),
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        });

      const newAssignments = await Promise.all(assignPromises);
      const remainingAssignments = assignedContainers.filter((assignment) =>
        targetContainers.includes(assignment.conId)
      );
      setAssignedContainers([...remainingAssignments, ...newAssignments]);
      toast.success('Containers updated successfully');
    } catch (error) {
      console.error('Error updating containers:', error);
      toast.error(`Failed to update containers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleContainerToggle = (conId) => {
    const newSelectedContainers = selectedContainers.includes(conId)
      ? selectedContainers.filter((id) => id !== conId)
      : [...selectedContainers, conId];

    setSelectedContainers(newSelectedContainers);
    handleSaveContainers(newSelectedContainers);
  };

  const handleContainerSelect = (e) => {
    setSelectedContainer(e.target.value);
  };

  const handleAssignContainer = async () => {
    if (!selectedContainer || !flowIdentity) {
      toast.warning('Please select a container');
      return;
    }
    setLoading(true);
    try {
      const assignmentData = {
        conId: selectedContainer,
        sflId: flowIdentity,
        ...(instanceId && { scaInstanceId: instanceId }),
      };
      const response = await fetch(CONTAINER_ASSIGNMENTS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(assignmentData),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      toast.success('Container assigned successfully');
      handleCloseAssignContainerModal();
    } catch (error) {
      console.error('Error assigning container:', error);
      toast.error(`Failed to assign container: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParameter = () => {
    if (!selectedParameter) {
      toast.warning('Please select a parameter');
      return;
    }

    // Find the selected parameter to check if it's RecordsToProcess
    const selectedParam = availableParameters.find((p) => p.mfpId === selectedParameter);
    if (!selectedParam) {
      toast.error('Selected parameter not found');
      return;
    }

    // Check if a parameter with the same name already exists for this flow
    const existingParam = parameters.find((p) => p.sfpName === selectedParam.mfpName);
    if (existingParam) {
      toast.warning(`Parameter "${getParameterDisplayName(selectedParam.mfpName)}" already exists. Please edit the existing parameter instead.`);
      return;
    }

    // Allow empty for RecordsToProcess (process all) and RunMode (backend defaults to Full)
    const allowEmpty = ['RecordsToProcess', 'RunMode'].includes(selectedParam.mfpName);
    if (!allowEmpty && !paramValue.trim()) {
      toast.warning('Parameter value is required');
      return;
    }
    const newParam = {
      sfpName: selectedParam.mfpName,
      sfpValue: paramValue,
      sfpMfId: masterFlowId,
      sfpMcId: selectedParam.mfpMcId,
      sfpId: generateUUID(),
      ...(instanceId && { sfpInstanceId: instanceId }),
    };
    saveParameters([...parameters, newParam]);
    handleCloseModal();
  };

  const handleEditParameter = (parameter) => {
    setCurrentParameter(parameter);
    setParamValue(parameter.sfpValue);
    setEditMode(true);
  };

  const handleUpdateParameter = async () => {
    // Allow empty values for RecordsToProcess parameter (empty means "process all records")
    if (!currentParameter) {
      toast.warning('No parameter selected for update');
      return;
    }

    // Allow empty for RecordsToProcess and RunMode (backend defaults)
    const allowEmpty = ['RecordsToProcess', 'RunMode'].includes(currentParameter.sfpName);
    if (!allowEmpty && !paramValue.trim()) {
      toast.warning('Parameter value is required');
      return;
    }

    setLoading(true);
    try {
      const updatedParam = {
        sfpId: currentParameter.sfpId,
        sfpName: currentParameter.sfpName,
        sfpValue: paramValue, // Allow empty string for RecordsToProcess
        sfpMfId: currentParameter.sfpMfId,
        sfpMcId: currentParameter.sfpMcId,
      };
      const response = await fetch(`${API_BASE_URL}/${currentParameter.sfpId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedParam),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updatedData = await response.json();
      setParameters(parameters.map((param) =>
        param.sfpId === currentParameter.sfpId ? updatedData : param
      ));
      toast.success('Parameter updated successfully');
      setEditMode(false);
      setCurrentParameter(null);
      setParamValue('');
    } catch (error) {
      console.error('Error updating parameter:', error);
      toast.error(`Failed to update parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParameter = async (parameterId) => {
    if (!window.confirm('Are you sure you want to delete this parameter?')) return;
    setLoading(true);
    try {
      const paramToDelete = parameters.find(p => p.sfpId === parameterId);

      // If deleting CopyFilesMappings, delete all chunks too
      if (paramToDelete && (paramToDelete.sfpName === 'CopyFilesMappings' || paramToDelete.sfpName?.startsWith('CopyFilesMappings_'))) {
        const allCopyFilesParams = parameters.filter(p =>
          p.sfpName === 'CopyFilesMappings' ||
          (p.sfpName && p.sfpName.startsWith('CopyFilesMappings_'))
        );

        // Delete all CopyFilesMappings parameters
        const deletePromises = allCopyFilesParams.map(p =>
          fetch(`${API_BASE_URL}/${p.sfpId}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          })
        );

        await Promise.all(deletePromises);
        setParameters(parameters.filter(p =>
          p.sfpName !== 'CopyFilesMappings' &&
          !p.sfpName?.startsWith('CopyFilesMappings_')
        ));
        toast.success('Copy Files Mappings deleted successfully');
      } else if (paramToDelete && (paramToDelete.sfpName === 'GoogleDriveMappings' || paramToDelete.sfpName?.startsWith('GoogleDriveMappings_'))) {
        // If deleting GoogleDriveMappings (all), delete all chunks too
        const allGoogleDriveParams = parameters.filter(p =>
          p.sfpName === 'GoogleDriveMappings' ||
          (p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
        );

        // Delete all GoogleDriveMappings parameters
        const deletePromises = allGoogleDriveParams.map(p =>
          fetch(`${API_BASE_URL}/${p.sfpId}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          })
        );

        await Promise.all(deletePromises);
        setParameters(parameters.filter(p =>
          p.sfpName !== 'GoogleDriveMappings' &&
          !p.sfpName?.startsWith('GoogleDriveMappings_')
        ));
        toast.success('Google Drive Mappings deleted successfully');
      } else {
        // Regular parameter deletion
        const response = await fetch(`${API_BASE_URL}/${parameterId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        setParameters(parameters.filter((param) => param.sfpId !== parameterId));
        toast.success('Parameter deleted successfully');
      }

      if (currentParameter && currentParameter.sfpId === parameterId) {
        setEditMode(false);
        setCurrentParameter(null);
        setParamValue('');
      }
    } catch (error) {
      console.error('Error deleting parameter:', error);
      toast.error(`Failed to delete parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveParameters = async (paramsToSave) => {
    setLoading(true);
    try {
      // When instanceId is set, ensure each property has sfpInstanceId for per-instance storage
      const payload = instanceId
        ? paramsToSave.map((p) => ({ ...p, sfpInstanceId: p.sfpInstanceId ?? instanceId }))
        : paramsToSave;
      const response = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Try to parse error message from response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const savedParams = await response.json();
      setParameters(savedParams);
      toast.success(editMode ? 'Parameter updated successfully' : 'Parameter added successfully');
    } catch (error) {
      console.error('Error saving parameters:', error);
      toast.error(`Failed to save parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setCurrentParameter(null);
    setParamValue('');
  };

  const getParameterTypeName = (mcId) => {
    return "";
  };

  // Helper function to format CopyFilesMappings for display
  const formatCopyFilesMappings = (paramValue) => {
    if (!paramValue || paramValue.trim() === '' || paramValue.trim() === '[]') {
      return ['No mappings configured'];
    }

    try {
      const mappings = JSON.parse(paramValue);
      if (!Array.isArray(mappings) || mappings.length === 0) {
        return ['No mappings configured'];
      }

      // Format as array of strings, one per mapping: ["Project A → Project B", "Project C → Project D"]
      return mappings.map(m => {
        // Helper to format project display name
        const formatProjectName = (projectName, projectId) => {
          // If name is missing, empty, or same as ID, show truncated ID with label
          if (!projectName || projectName.trim() === '' || projectName === projectId) {
            if (projectId) {
              // Show first 12 chars of ID for readability, or full ID if short
              const shortId = projectId.length > 12 ? `${projectId.substring(0, 12)}...` : projectId;
              return `[ID: ${shortId}]`;
            }
            return '[Unknown]';
          }
          return projectName;
        };

        const srcName = formatProjectName(m.srcProjectName, m.srcProjectId);
        const dstName = formatProjectName(m.dstProjectName, m.dstProjectId);

        return `${srcName} → ${dstName}`;
      });
    } catch (e) {
      // If parsing fails, return truncated value
      const displayValue = paramValue.length > 50 ? paramValue.substring(0, 50) + '...' : paramValue;
      return [displayValue];
    }
  };

  // Helper function to format GoogleDriveMappings for display
  const formatGoogleDriveMappings = (paramValue) => {
    if (!paramValue || paramValue.trim() === '' || paramValue.trim() === '[]') {
      return ['No mappings configured'];
    }

    try {
      const mappings = JSON.parse(paramValue);
      if (!Array.isArray(mappings) || mappings.length === 0) {
        return ['No mappings configured'];
      }

      // Format as array of strings, one per mapping: ["Project A → Google Drive Folder", "Project B → Root"]
      return mappings.map(m => {
        // Helper to format project display name
        const formatProjectName = (projectName, projectId) => {
          // If name is missing, empty, or same as ID, show truncated ID with label
          if (!projectName || projectName.trim() === '' || projectName === projectId) {
            if (projectId) {
              // Show first 12 chars of ID for readability, or full ID if short
              const shortId = projectId.length > 12 ? `${projectId.substring(0, 12)}...` : projectId;
              return `[ID: ${shortId}]`;
            }
            return '[Unknown]';
          }
          return projectName;
        };

        // Helper to format Google Drive folder display
        const formatGoogleDriveFolder = (folderId, folderName) => {
          if (!folderId || folderId.trim() === '') {
            return 'Root (My Drive)';
          }

          // If we have a folder name stored, use it (preferred)
          if (folderName && folderName.trim() !== '') {
            return folderName;
          }

          // Otherwise, show truncated ID as fallback
          const shortId = folderId.length > 20 ? `${folderId.substring(0, 20)}...` : folderId;
          return `Folder: ${shortId}`;
        };

        const srcName = formatProjectName(m.srcProjectName, m.srcProjectId);
        // Check for both dstGoogleDriveFolderId and dstFolderId (for backward compatibility)
        const targetFolderId = m.dstGoogleDriveFolderId || m.dstFolderId;
        // Check for folder name (stored when mapping was saved)
        const targetFolderName = m.dstGoogleDriveFolderName || m.dstFolderName;
        const targetFolder = formatGoogleDriveFolder(targetFolderId, targetFolderName);

        const baseLabel = `${srcName} → ${targetFolder}`;
        if (m.mappingName && m.mappingName.trim().length > 0) {
          return `${m.mappingName.trim()} — ${baseLabel}`;
        }
        return baseLabel;
      });
    } catch (e) {
      // If parsing fails, return truncated value
      const displayValue = paramValue.length > 50 ? paramValue.substring(0, 50) + '...' : paramValue;
      return [displayValue];
    }
  };

  const shouldShowSection = (sectionKey) => {
    // Prefer Master Flow UI config; if absent, keep previous default (show).
    if (uiConfig?.sections && typeof uiConfig.sections[sectionKey] === 'boolean') {
      return uiConfig.sections[sectionKey];
    }
    return true;
  };

  const shouldShowContainersSection = () => shouldShowSection('containers');

  // Check if this is a copy files flow
  const isCopyFilesFlow = () => {
    if (!masterFlowName) return false;
    return masterFlowName.toLowerCase().includes('copy') ||
      masterFlowName.toLowerCase().includes('file');
  };

  // Check if this is a Google Drive flow
  const isGoogleDriveFlow = () => {
    if (!masterFlowName) {
      // Fallback: Check if GoogleDriveMappings parameter exists
      const hasMappings = parameters.some(p =>
        p.sfpName === 'GoogleDriveMappings' ||
        (p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
      );
      return hasMappings;
    }
    // Check masterFlowName for Google Drive keywords
    const flowNameLower = masterFlowName.toLowerCase();
    // Check for "google drive" as a phrase or both words separately
    const isGoogleDrive = flowNameLower.includes('google drive') ||
      (flowNameLower.includes('google') && flowNameLower.includes('drive'));
    return isGoogleDrive;
  };


  // Helper function to check if parameter should be hidden (legacy copy files params)
  const shouldHideParameter = (paramName) => {
    // Only hide legacy params for copy files flows
    if (!isCopyFilesFlow()) return false;
    const legacyParams = ['srcProjectId', 'srcFolderId', 'dstProjectId', 'dstFolderId'];
    return legacyParams.includes(paramName);
  };

  // Helper function to get filtered parameters (excluding legacy copy files params)
  const getFilteredParameters = () => {
    return parameters.filter(param => !shouldHideParameter(param.sfpName));
  };

  // Helper function to get CopyFilesMappings display (combining chunked parts if needed)
  const getCopyFilesMappingsDisplay = () => {
    // Check for chunked parts first
    const chunkedParams = parameters
      .filter(p => p.sfpName && p.sfpName.startsWith('CopyFilesMappings_'))
      .sort((a, b) => {
        try {
          const ia = parseInt(a.sfpName.substring('CopyFilesMappings_'.length), 10);
          const ib = parseInt(b.sfpName.substring('CopyFilesMappings_'.length), 10);
          return ia - ib;
        } catch { return a.sfpName.localeCompare(b.sfpName); }
      });

    if (chunkedParams.length > 0) {
      const combinedJson = chunkedParams.map(p => p.sfpValue).join('');
      return formatCopyFilesMappings(combinedJson);
    }

    // Otherwise look for single CopyFilesMappings parameter
    const mappingsParam = parameters.find(p => p.sfpName === 'CopyFilesMappings');
    if (mappingsParam) {
      return formatCopyFilesMappings(mappingsParam.sfpValue);
    }

    return null;
  };

  // Helper function to get GoogleDriveMappings raw data (combining chunked parts if needed)
  const getGoogleDriveMappingsData = () => {
    // Check for chunked parts first
    const chunkedParams = parameters
      .filter(p => p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
      .sort((a, b) => {
        try {
          const ia = parseInt(a.sfpName.substring('GoogleDriveMappings_'.length), 10);
          const ib = parseInt(b.sfpName.substring('GoogleDriveMappings_'.length), 10);
          return ia - ib;
        } catch { return a.sfpName.localeCompare(b.sfpName); }
      });

    if (chunkedParams.length > 0) {
      const combinedJson = chunkedParams.map(p => p.sfpValue).join('');
      try {
        const mappings = JSON.parse(combinedJson);
        return Array.isArray(mappings) ? mappings : [];
      } catch {
        return [];
      }
    }

    // Otherwise look for single GoogleDriveMappings parameter
    const mappingsParam = parameters.find(p => p.sfpName === 'GoogleDriveMappings' && p.sfpValue !== '[]');
    if (mappingsParam && mappingsParam.sfpValue && mappingsParam.sfpValue !== '[]') {
      try {
        const mappings = JSON.parse(mappingsParam.sfpValue);
        return Array.isArray(mappings) ? mappings : [];
      } catch {
        return [];
      }
    }

    return [];
  };

  // Helper function to get GoogleDriveMappings display (combining chunked parts if needed)
  const getGoogleDriveMappingsDisplay = () => {
    const mappingsData = getGoogleDriveMappingsData();
    if (mappingsData.length === 0) {
      return null;
    }
    // Format the mappings data for display
    const jsonString = JSON.stringify(mappingsData);
    return formatGoogleDriveMappings(jsonString);
  };

  // Handle deleting a single GoogleDriveMappings pair
  const handleDeleteGoogleDriveMappingPair = async (mappingIndex) => {
    if (!window.confirm('Are you sure you want to delete this mapping pair?')) return;

    setLoading(true);
    try {
      // Get current mappings data
      const currentMappings = getGoogleDriveMappingsData();
      if (mappingIndex < 0 || mappingIndex >= currentMappings.length) {
        toast.error('Invalid mapping index');
        return;
      }

      // Remove the mapping at the specified index
      const updatedMappings = currentMappings.filter((_, idx) => idx !== mappingIndex);

      // Get connector ID from existing parameter
      const existingParam = parameters.find(p =>
        p.sfpName === 'GoogleDriveMappings' ||
        (p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
      );
      const connectorId = existingParam?.sfpMcId;

      // If no mappings left, delete all parameters
      if (updatedMappings.length === 0) {
        const allGoogleDriveParams = parameters.filter(p =>
          p.sfpName === 'GoogleDriveMappings' ||
          (p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
        );

        const deletePromises = allGoogleDriveParams.map(p =>
          fetch(`${API_BASE_URL}/${p.sfpId}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          })
        );

        await Promise.all(deletePromises);
        setParameters(parameters.filter(p =>
          p.sfpName !== 'GoogleDriveMappings' &&
          !p.sfpName?.startsWith('GoogleDriveMappings_')
        ));
        toast.success('Mapping pair deleted successfully');
        return;
      }

      // Save updated mappings (will re-chunk if needed)
      const jsonString = JSON.stringify(updatedMappings);
      const paramsToSave = [];
      const MAX_CHUNK = 240;

      if (jsonString.length <= MAX_CHUNK) {
        // Single parameter is enough
        const mappingParam = {
          sfpName: 'GoogleDriveMappings',
          sfpValue: jsonString,
          sfpMfId: masterFlowId,
          sfpId: generateUUID()
        };
        if (connectorId) mappingParam.sfpMcId = connectorId;
        paramsToSave.push(mappingParam);
      } else {
        // Need to chunk
        const chunkSize = MAX_CHUNK;
        let index = 1;
        for (let i = 0; i < jsonString.length; i += chunkSize) {
          const chunk = jsonString.substring(i, i + chunkSize);
          const partParam = {
            sfpName: `GoogleDriveMappings_${index}`,
            sfpValue: chunk,
            sfpMfId: masterFlowId,
            sfpId: generateUUID()
          };
          if (connectorId) partParam.sfpMcId = connectorId;
          paramsToSave.push(partParam);
          index++;
        }
        // Also write a small marker row
        paramsToSave.push({
          sfpName: 'GoogleDriveMappings',
          sfpValue: '[]',
          sfpMfId: masterFlowId,
          sfpId: generateUUID(),
          ...(connectorId ? { sfpMcId: connectorId } : {})
        });
      }

      // First, delete all existing GoogleDriveMappings parameters
      const allGoogleDriveParams = parameters.filter(p =>
        p.sfpName === 'GoogleDriveMappings' ||
        (p.sfpName && p.sfpName.startsWith('GoogleDriveMappings_'))
      );

      const deletePromises = allGoogleDriveParams.map(p =>
        fetch(`${API_BASE_URL}/${p.sfpId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
      );

      await Promise.all(deletePromises);

      // Then, save the updated mappings (include instanceId for per-instance storage when set)
      const savePayload = instanceId
        ? paramsToSave.map((p) => ({ ...p, sfpInstanceId: p.sfpInstanceId ?? instanceId }))
        : paramsToSave;
      const saveResponse = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(savePayload),
      });

      if (!saveResponse.ok) {
        throw new Error(`HTTP error! status: ${saveResponse.status}`);
      }

      const savedParams = await saveResponse.json();

      // Update parameters state - remove old GoogleDriveMappings and add new ones
      const filteredParams = parameters.filter(p =>
        p.sfpName !== 'GoogleDriveMappings' &&
        !p.sfpName?.startsWith('GoogleDriveMappings_')
      );
      setParameters([...filteredParams, ...savedParams]);

      toast.success('Mapping pair deleted successfully');
    } catch (error) {
      console.error('Error deleting mapping pair:', error);
      toast.error(`Failed to delete mapping pair: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleRunStatusRowClick = async (record) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${RUN_STATUS_API_URL}/${record.srsId}/details`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSelectedRunDetails(data);
      setDetailsPopupOpen(true);
    } catch (error) {
      console.error('Error fetching run details:', error);
      toast.error(`Failed to load run details: ${error.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleErrorRecordsPageChange = (_event, newPage) => {
    setErrorRecordsPage(newPage);
  };

  const handleErrorRecordsPageSizeChange = (event) => {
    setErrorRecordsPageSize(parseInt(event.target.value, 10));
    setErrorRecordsPage(0);
  };

  const handleSystemErrorsPageChange = (_event, newPage) => {
    setSystemErrorsPage(newPage);
  };

  const handleSystemErrorsPageSizeChange = (event) => {
    setSystemErrorsPageSize(parseInt(event.target.value, 10));
    setSystemErrorsPage(0);
  };

  const handleRunStatusPageChange = (_event, newPage) => {
    setRunStatusPage(newPage);
  };

  const handleRunStatusPageSizeChange = (event) => {
    setRunStatusPageSize(parseInt(event.target.value, 10));
    setRunStatusPage(0);
  };

  const handleTrackRecordsPageChange = (_event, newPage) => {
    setTrackRecordsPage(newPage);
  };

  const handleTrackRecordsPageSizeChange = (event) => {
    setTrackRecordsPageSize(parseInt(event.target.value, 10));
    setTrackRecordsPage(0);
  };

  const handleDataRecordsPageChange = (_event, newPage) => {
    setDataRecordsPage(newPage);
  };

  const handleDataRecordsPageSizeChange = (event) => {
    setDataRecordsPageSize(parseInt(event.target.value, 10));
    setDataRecordsPage(0);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ '& .MuiDrawer-paper': { width: 900 } }} // Increased width to accommodate new columns
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <Settings sx={{ mr: 1 }} />
              <Typography variant="h6">Flow Details</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            Master Flow: {masterFlowName || 'Unknown'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Parameters section — gated by Master Flow UI config */}
          {shouldShowSection('parameters') && (
            <Accordion
              expanded={expandedAccordion === 'parameters'}
              onChange={handleAccordionChange('parameters')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Parameters</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Traditional parameter management */}
                <>
                  {editMode ? (
                    <Box mb={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        Edit Parameter: {getParameterDisplayName(currentParameter?.sfpName)}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="Parameter Value"
                        value={paramValue}
                        onChange={(e) => setParamValue(e.target.value)}
                        placeholder="Enter parameter value"
                        disabled={loading}
                        sx={{ mt: 1, mb: 1 }}
                      />
                      <Box display="flex" mt={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleUpdateParameter}
                          disabled={loading || (!['RecordsToProcess', 'RunMode'].includes(currentParameter?.sfpName) && !paramValue.trim())}
                          sx={{ mr: 1 }}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Update'}
                        </Button>
                        <Button variant="outlined" onClick={handleCancel}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box mb={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleOpenAddModal}
                        disabled={loading}
                      >
                        Add Parameter
                      </Button>
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Parameter List
                  </Typography>
                  {loading && !parameters.length ? (
                    <Box display="flex" justifyContent="center" my={4}>
                      <CircularProgress />
                    </Box>
                  ) : parameters.length === 0 ? (
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        No parameters found for this flow
                      </Typography>
                    </Paper>
                  ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {/* Display CopyFilesMappings in a user-friendly format if it exists (only for copy files flows) */}
                      {(() => {
                        // Only show special formatting for copy files flows
                        if (!isCopyFilesFlow()) return null;

                        const copyFilesDisplay = getCopyFilesMappingsDisplay();
                        const hasCopyFilesMappings = parameters.some(p =>
                          p.sfpName === 'CopyFilesMappings' ||
                          (p.sfpName && p.sfpName.startsWith('CopyFilesMappings_'))
                        );

                        if (hasCopyFilesMappings && copyFilesDisplay) {
                          // Find the first CopyFilesMappings parameter (could be marker or chunk)
                          const copyFilesParam = parameters.find(p =>
                            p.sfpName === 'CopyFilesMappings' ||
                            (p.sfpName && p.sfpName.startsWith('CopyFilesMappings_'))
                          );

                          // Get connector ID from any CopyFilesMappings parameter
                          const connectorId = copyFilesParam?.sfpMcId ||
                            parameters.find(p => p.sfpName?.startsWith('CopyFilesMappings_'))?.sfpMcId;

                          return (
                            <React.Fragment key="copyfilesmappings">
                              <ListItem
                                secondaryAction={
                                  <Box>
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      disabled
                                      title="Edit CopyFilesMappings in Data Point Details page"
                                      sx={{ opacity: 0.5 }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={() => {
                                        const paramToDelete = parameters.find(p =>
                                          p.sfpName === 'CopyFilesMappings' ||
                                          (p.sfpName && p.sfpName.startsWith('CopyFilesMappings_'))
                                        );
                                        if (paramToDelete) {
                                          handleDeleteParameter(paramToDelete.sfpId);
                                        }
                                      }}
                                      title="Delete Copy Files Mappings"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                }
                              >
                                <ListItemText
                                  primary="Copy Files Mappings"
                                  secondary={
                                    <>
                                      <Typography component="span" variant="body2" color="text.primary">
                                        {getParameterTypeName(connectorId)}
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        {Array.isArray(copyFilesDisplay) ? (
                                          copyFilesDisplay.map((mapping, idx) => (
                                            <Typography
                                              key={idx}
                                              component="div"
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ fontWeight: 500 }}
                                            >
                                              {mapping}
                                            </Typography>
                                          ))
                                        ) : (
                                          <Typography
                                            component="span"
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                          >
                                            {copyFilesDisplay}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  }
                                />
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          );
                        }
                        return null;
                      })()}

                      {/* Display GoogleDriveMappings (similar to CopyFilesMappings) */}
                      {(() => {
                        const googleDriveDisplay = getGoogleDriveMappingsDisplay();
                        const googleDriveMappingsData = getGoogleDriveMappingsData();
                        if (googleDriveDisplay && googleDriveMappingsData.length > 0) {
                          const connectorId = parameters.find(p =>
                            p.sfpName === 'GoogleDriveMappings' ||
                            p.sfpName?.startsWith('GoogleDriveMappings_')
                          )?.sfpMcId;

                          return (
                            <React.Fragment key="googledrivemappings">
                              <ListItem>
                                <ListItemText
                                  primary="Google Drive Mappings"
                                  secondary={
                                    <>
                                      <Typography component="span" variant="body2" color="text.primary">
                                        {getParameterTypeName(connectorId)}
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        {Array.isArray(googleDriveDisplay) ? (
                                          googleDriveDisplay.map((mapping, idx) => (
                                            <Box
                                              key={idx}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 0.5,
                                                '&:hover': { backgroundColor: 'action.hover' }
                                              }}
                                            >
                                              <Typography
                                                component="div"
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500, flex: 1 }}
                                              >
                                                {mapping}
                                              </Typography>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleDeleteGoogleDriveMappingPair(idx)}
                                                title="Delete this mapping pair"
                                                disabled={loading}
                                                sx={{ ml: 1 }}
                                              >
                                                <Delete fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          ))
                                        ) : (
                                          <Typography
                                            component="span"
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                          >
                                            {googleDriveDisplay}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  }
                                />
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          );
                        }
                        return null;
                      })()}

                      {/* Display other parameters (excluding legacy copy files params, CopyFilesMappings chunks, and GoogleDriveMappings chunks) */}
                      {getFilteredParameters()
                        .filter(param =>
                          param.sfpName !== 'CopyFilesMappings' &&
                          !param.sfpName?.startsWith('CopyFilesMappings_') &&
                          param.sfpName !== 'GoogleDriveMappings' &&
                          !param.sfpName?.startsWith('GoogleDriveMappings_')
                        )
                        .map((param) => {
                          // AccountId should not have edit/delete options for copy files flows or Google Drive flows
                          const isAccountId = param.sfpName === 'AccountId';
                          const isCopyFiles = isCopyFilesFlow();
                          const isGoogleDrive = isGoogleDriveFlow();
                          const shouldHideActions = isAccountId && (isCopyFiles || isGoogleDrive);

                          return (
                            <React.Fragment key={param.sfpId}>
                              <ListItem
                                secondaryAction={
                                  !shouldHideActions ? (
                                    <Box>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => handleEditParameter(param)}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => handleDeleteParameter(param.sfpId)}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ) : null
                                }
                              >
                                <ListItemText
                                  primary={`${getParameterDisplayName(param.sfpName)} - ${param.sfpValue || 'N/A'}`}
                                  secondary={
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {getParameterTypeName(param.sfpMcId)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          );
                        })}
                    </List>
                  )}
                </>
              </AccordionDetails>
            </Accordion>
          )}

          {shouldShowContainersSection() && (
            <Accordion
              expanded={expandedAccordion === 'containers'}
              onChange={handleAccordionChange('containers')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Containers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" gutterBottom>
                  Available Containers
                </Typography>
                {loadingContainers ? (
                  <Box display="flex" justifyCenter="center" my={4}>
                    <CircularProgress />
                  </Box>
                ) : containers.length === 0 ? (
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      No containers available
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <Grid container spacing={2}>
                      {containers.map((container) => (
                        <Grid item xs={12} key={container.conId}>
                          <Box display="flex" alignItems="center">
                            <Checkbox
                              checked={selectedContainers.includes(container.conId)}
                              onChange={() => handleContainerToggle(container.conId)}
                              disabled={loading}
                            />
                            <Typography>{container.conDescription}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {shouldShowSection('assignedConnectors') && (
          <Accordion
            expanded={expandedAccordion === 'assignedConnectors'}
            onChange={handleAccordionChange('assignedConnectors')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Assigned Connectors</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loadingAssignedConnectors ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Assign Connectors for this Flow:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {Array.isArray(masterConnectors) && masterConnectors.map((connector) => (
                        <Button
                          key={connector.mcId}
                          variant="outlined"
                          color="primary"
                          startIcon={<Add />}
                          onClick={() => handleOpenConnectorModal(connector.mcName)}
                          size="small"
                        >
                          {connector.mcName}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {assignedConnectorsData.length === 0 ? (
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        No connectors assigned to this flow
                      </Typography>
                    </Paper>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Connector Type</strong></TableCell>
                            <TableCell><strong>Connector Description</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assignedConnectorsData.map((assignment) => (
                            <TableRow key={assignment.sfcaId}>
                              <TableCell>{assignment.connectorType}</TableCell>
                              <TableCell>{assignment.connectorDescription || 'N/A'}</TableCell>
                              <TableCell>{assignment.connectorStatus}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenConnectorModal(assignment.connectorType)}
                                  title="Change Connector"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveConnectorAssignment(assignment.connectorType)}
                                  title="Remove Assignment"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {shouldShowSection('syncTrackRecords') && (
          <Accordion
            expanded={expandedAccordion === 'trackRecords'}
            onChange={handleAccordionChange('trackRecords')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Sync Track Records</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !trackRecords.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : trackRecords.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No track records found for this flow
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source ID</TableCell>
                        <TableCell>Document Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trackRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>{record.sourceId}</TableCell>
                          <TableCell>{record.documentType}</TableCell>
                          <TableCell>{record.status || 'N/A'}</TableCell>
                          <TableCell>{record.message || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={trackRecordsTotal}
                    page={trackRecordsPage}
                    onPageChange={handleTrackRecordsPageChange}
                    rowsPerPage={trackRecordsPageSize}
                    onRowsPerPageChange={handleTrackRecordsPageSizeChange}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
                  />
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {shouldShowSection('validationFailed') && (
          <Accordion
            expanded={expandedAccordion === 'errorRecords'}
            onChange={handleAccordionChange('errorRecords')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Validation Failed</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !errorRecords.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : errorRecords.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No error records found for this flow
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        {/* <TableCell>Source Description</TableCell>
                        <TableCell>Target Description</TableCell> */}
                        <TableCell>Message</TableCell>
                        <TableCell>Sync DateTime</TableCell>
                        <TableCell>Flow ID</TableCell>
                        <TableCell>Document Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>{record.id}</TableCell>
                          {/* <TableCell>{record.sourceDesc || 'N/A'}</TableCell>
                          <TableCell>{record.targetDesc || 'N/A'}</TableCell> */}
                          <TableCell>{record.message}</TableCell>
                          <TableCell>
                            {formatAppDateTime(record.syncDateTime)}
                          </TableCell>
                          <TableCell>{record.sflId}</TableCell>
                          <TableCell>{record.documentType}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={errorRecordsTotal}
                    page={errorRecordsPage}
                    onPageChange={handleErrorRecordsPageChange}
                    rowsPerPage={errorRecordsPageSize}
                    onRowsPerPageChange={handleErrorRecordsPageSizeChange}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
                  />
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {shouldShowSection('systemError') && (
          <Accordion
            expanded={expandedAccordion === 'systemErrors'}
            onChange={handleAccordionChange('systemErrors')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>System Error</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !systemErrors.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : systemErrors.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No system errors found for this flow
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Message</TableCell>
                        <TableCell>API Details</TableCell>
                        <TableCell>Line</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {systemErrors.map((error) => (
                        <TableRow key={error.id}>
                          <TableCell>{error.message || 'N/A'}</TableCell>
                          <TableCell>{error.apiDetails || 'N/A'}</TableCell>
                          <TableCell>{error.line || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={systemErrorsTotal}
                    page={systemErrorsPage}
                    onPageChange={handleSystemErrorsPageChange}
                    rowsPerPage={systemErrorsPageSize}
                    onRowsPerPageChange={handleSystemErrorsPageSizeChange}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
                  />
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {shouldShowSection('syncRunStatus') && (
          <Accordion
            expanded={expandedAccordion === 'runStatus'}
            onChange={handleAccordionChange('runStatus')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Sync Run Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !runStatusRecords.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : runStatusRecords.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No run status records found for this flow
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Start DateTime</TableCell>
                        <TableCell>End DateTime</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Run Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRunStatusRecords.map((record) => (
                        <TableRow
                          key={record.srsId}
                          onClick={() => handleRunStatusRowClick(record)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        >
                          <TableCell>
                            {formatAppDateTime(record.srsStartDateTime)}
                          </TableCell>
                          <TableCell>
                            {record.srsEndDateTime
                              ? formatAppDateTime(record.srsEndDateTime)
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(record.srsStatus === 'COMPLETED' || record.srsStatus === 'success') ? 'SUCCESS' : record.srsStatus}
                              size="small"
                              color={
                                record.srsStatus === 'SUCCESS' || record.srsStatus === 'COMPLETED' || record.srsStatus === 'success' ? 'success' :
                                  record.srsStatus === 'ERROR' || record.srsStatus === 'FAILED' ? 'error' :
                                    record.srsStatus === 'RUNNING' ? 'info' : 'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const remarks = record.srsRemarks || '';
                              // Look for "Response:" marker (case-insensitive, optional space after colon)
                              const markerRegex = /Response:\s*/i;
                              const match = remarks.match(markerRegex);

                              if (match) {
                                const splitIndex = match.index;
                                const messagePart = remarks.substring(0, splitIndex).trim();
                                const jsonPart = remarks.substring(splitIndex + match[0].length).trim();

                                return (
                                  <Box display="flex" alignItems="center">
                                    <Typography variant="body2">{messagePart || 'Flow executed successfully'}</Typography>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        try {
                                          // Try to parse as JSON if it looks like it, otherwise use raw string
                                          if (jsonPart.startsWith('{') || jsonPart.startsWith('[')) {
                                            setSelectedResponseJson(JSON.parse(jsonPart));
                                          } else {
                                            setSelectedResponseJson(jsonPart);
                                          }
                                        } catch (err) {
                                          setSelectedResponseJson(jsonPart);
                                        }
                                        setResponseModalOpen(true);
                                      }}
                                      sx={{ ml: 1 }}
                                      title="View Response"
                                    >
                                      <RemoveRedEye fontSize="small" />
                                    </IconButton>
                                  </Box>
                                );
                              }
                              return remarks;
                            })()}
                          </TableCell>
                          <TableCell>{record.srsRunType}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={runStatusTotal}
                    page={runStatusPage}
                    onPageChange={handleRunStatusPageChange}
                    rowsPerPage={runStatusPageSize}
                    onRowsPerPageChange={handleRunStatusPageSizeChange}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
                  />
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {shouldShowSection('data') && (
          <Accordion
            expanded={expandedAccordion === 'dataRecords'}
            onChange={handleAccordionChange('dataRecords')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !dataRecords.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : dataRecords.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No data records found for this flow
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <TableContainer component={Paper}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>Revision</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dataRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{record.dataDescription}</TableCell>
                            <TableCell>{record.dataCode}</TableCell>
                            <TableCell>{record.dataRevision}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={dataRecordsTotal}
                      page={dataRecordsPage}
                      onPageChange={handleDataRecordsPageChange}
                      rowsPerPage={dataRecordsPageSize}
                      onRowsPerPageChange={handleDataRecordsPageSizeChange}
                      rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
                    />
                  </TableContainer>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          )}
        </Box>
      </Drawer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Parameter</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="parameter-select-label">Parameter</InputLabel>
            <Select
              labelId="parameter-select-label"
              value={selectedParameter}
              onChange={handleParameterSelect}
              label="Parameter"
              disabled={loadingParameters}
            >
              {loadingParameters ? (
                <MenuItem value="">Loading parameters...</MenuItem>
              ) : availableParameters.length === 0 ? (
                <MenuItem value="">No parameters available</MenuItem>
              ) : (
                availableParameters.map((param) => (
                  /* <MenuItem key={param.mfpId} value={param.mfpId}>
                     {param.mfpName} ({getParameterTypeName(param.mfpMcId)})
                   </MenuItem>*/

                  <MenuItem key={param.mfpId} value={param.mfpId}>
                    {getParameterDisplayName(param.mfpName)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Parameter Value"
            value={paramValue}
            onChange={(e) => setParamValue(e.target.value)}
            placeholder="Enter parameter value"
            disabled={!selectedParameter}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleAddParameter}
            variant="contained"
            color="primary"
            disabled={loading || !selectedParameter || (selectedParameter && !['RecordsToProcess', 'RunMode'].includes(availableParameters.find(p => p.mfpId === selectedParameter)?.mfpName || '') && !paramValue.trim())}
          >
            {loading ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={connectorModalOpen}
        onClose={() => {
          setConnectorModalOpen(false);
          setSelectedConnectorId('');
          setSelectedConnectorType('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Connector - {selectedConnectorType}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="connector-select-label">Select Connector Instance</InputLabel>
            <Select
              labelId="connector-select-label"
              value={selectedConnectorId || ''}
              onChange={(e) => setSelectedConnectorId(e.target.value)}
              label="Select Connector Instance"
              disabled={loadingAssignedConnectors}
            >
              {loadingAssignedConnectors ? (
                <MenuItem key="loading" value="">Loading connectors...</MenuItem>
              ) : availableConnectorsForAssignment.length === 0 ? (
                <MenuItem key="empty" value="">No connectors available</MenuItem>
              ) : (
                availableConnectorsForAssignment.map((connector) => (
                  <MenuItem key={connector.id} value={connector.id}>
                    {connector.description || `Connector ${connector.id.substring(0, 8)}`}
                    {' - '}
                    ({connector.status})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConnectorModalOpen(false);
              setSelectedConnectorId('');
              setSelectedConnectorType('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignConnector}
            variant="contained"
            color="primary"
            disabled={loadingAssignedConnectors || !selectedConnectorId}
          >
            {loadingAssignedConnectors ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignContainerModalOpen} onClose={handleCloseAssignContainerModal} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Container</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="container-select-label">Container</InputLabel>
            <Select
              labelId="container-select-label"
              value={selectedContainer}
              onChange={handleContainerSelect}
              label="Container"
              disabled={loadingContainers}
            >
              {loadingContainers ? (
                <MenuItem value="">Loading containers...</MenuItem>
              ) : containers.length === 0 ? (
                <MenuItem value="">No containers available</MenuItem>
              ) : (
                containers.map((container) => (
                  <MenuItem key={container.conId} value={container.conId}>
                    {container.conDescription}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignContainerModal}>Cancel</Button>
          <Button
            onClick={handleAssignContainer}
            variant="contained"
            color="primary"
            disabled={!selectedContainer || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={responseModalOpen}
        onClose={() => setResponseModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Response JSON</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              p: 2,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', // Wrap text to avoid horizontal scroll
              wordBreak: 'break-word', // Break long words if necessary
              maxHeight: '60vh',
              overflowY: 'auto',
              overflowX: 'hidden', // Explicitly hide horizontal scroll
            }}
          >
            {typeof selectedResponseJson === 'object'
              ? JSON.stringify(selectedResponseJson, null, 2)
              : selectedResponseJson}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsPopupOpen}
        onClose={() => setDetailsPopupOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Sync Run Details
          <IconButton
            onClick={() => setDetailsPopupOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : selectedRunDetails ? (
            <Grid container spacing={2}>
              {/* Sync Tracks Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Sync Tracks</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source ID</TableCell>
                          <TableCell>Document Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRunDetails.syncTracks.map((track) => (
                          <TableRow key={track.id}>
                            <TableCell>{track.sourceId}</TableCell>
                            <TableCell>{track.documentType}</TableCell>
                            <TableCell>{track.status || 'N/A'}</TableCell>
                            <TableCell>{track.message || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Error Records Section */}
              {selectedRunDetails.errorRecords.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Error Records</Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            {/* <TableCell>Source Description</TableCell>
                            <TableCell>Target Description</TableCell> */}
                            <TableCell>Message</TableCell>
                            <TableCell>Sync DateTime</TableCell>
                            <TableCell>Flow ID</TableCell>
                            <TableCell>Document Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRunDetails.errorRecords.map((error) => (
                            <TableRow key={error.id}>
                              <TableCell>{error.id}</TableCell>
                              {/* <TableCell>{error.sourceDesc || 'N/A'}</TableCell>
                              <TableCell>{error.targetDesc || 'N/A'}</TableCell> */}
                              <TableCell>{error.message}</TableCell>
                              <TableCell>{formatAppDateTime(error.syncDateTime)}</TableCell>
                              <TableCell>{error.sflId}</TableCell>
                              <TableCell>{error.documentType}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}

              {/* Data Records Section */}
              {selectedRunDetails.dataRecords && selectedRunDetails.dataRecords.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Data Records</Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Revision</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRunDetails.dataRecords.map((record) => (
                            <TableRow key={record.id}>
                              <TableCell>{record.dataDescription}</TableCell>
                              <TableCell>{record.dataCode}</TableCell>
                              <TableCell>{record.dataRevision}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography color="textSecondary">No details available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyncFlowParameter;